import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { enableAnalytics } from '../utils/analytics';

const STORAGE_KEY = 'mars_cookie_consent';

export const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ functional: true, analytics: false });
  const navigate = useNavigate();

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) setVisible(true);
  }, []);

  const save = (choice) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(choice));
    if (choice.analytics) enableAnalytics();
    setVisible(false);
    setShowPrefs(false);
  };

  const acceptAll  = () => save({ functional: true, analytics: true,  all: true  });
  const rejectAll  = () => save({ functional: true, analytics: false, all: false });
  const savePrefs  = () => save({ ...prefs, all: false });

  if (!visible) return null;

  const btnPrimary = {
    background: '#1a3a6b', color: 'white', border: 'none', borderRadius: 8,
    padding: '11px 22px', fontWeight: 700, fontSize: 14, cursor: 'pointer',
    fontFamily: 'inherit', transition: 'background 0.15s',
  };
  const btnSecondary = {
    background: '#1a3a6b', color: 'white', border: 'none', borderRadius: 8,
    padding: '11px 22px', fontWeight: 700, fontSize: 14, cursor: 'pointer',
    fontFamily: 'inherit', transition: 'background 0.15s',
  };
  const btnOutline = {
    background: '#F1F5F9', color: '#1E3A5F', border: '1.5px solid #CBD5E1', borderRadius: 8,
    padding: '11px 22px', fontWeight: 600, fontSize: 14, cursor: 'pointer',
    fontFamily: 'inherit', transition: 'all 0.15s',
  };

  return (
    <>
      <style>{`
        .cookie-banner {
          position: fixed; bottom: 0; left: 0; right: 0;
          background: white; border-top: 1px solid #E2E8F0;
          box-shadow: 0 -8px 32px rgba(0,0,0,0.12);
          padding: 24px 40px; z-index: 9999;
          font-family: Inter, system-ui, sans-serif;
          display: flex; align-items: center; gap: 32; flex-wrap: wrap;
        }
        .cookie-btns {
          display: flex; gap: 10px; flex-shrink: 0; flex-wrap: wrap;
        }
        .cookie-pref-modal {
          position: fixed; top: 50%; left: 50%; transform: translate(-50%,-50%);
          background: white; border-radius: 16px; box-shadow: 0 24px 64px rgba(0,0,0,0.2);
          padding: 36px 32px; width: calc(100% - 32px); max-width: 480px; z-index: 9999;
          font-family: Inter, system-ui, sans-serif; box-sizing: border-box;
          max-height: 90vh; overflow-y: auto;
        }
        @media (max-width: 640px) {
          .cookie-banner { padding: 18px 16px !important; gap: 14px !important; }
          .cookie-btns { width: 100% !important; }
          .cookie-btns button { flex: 1 !important; padding: 10px 10px !important; font-size: 13px !important; }
          .cookie-pref-modal { padding: 24px 18px !important; }
        }
        @media (max-width: 400px) {
          .cookie-banner { padding: 14px 12px !important; }
          .cookie-btns button { font-size: 12px !important; padding: 9px 8px !important; }
        }
      `}</style>

      {/* Backdrop blur for preferences modal */}
      {showPrefs && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 9998 }}
          onClick={() => setShowPrefs(false)} />
      )}

      {/* ── Main banner ── */}
      <div className="cookie-banner">
        {/* Text */}
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: 17, fontWeight: 800, color: '#0F172A' }}>
              Cookies improve your experience
            </span>
            <ShieldCheck size={17} color="#16A34A" strokeWidth={2.5} />
          </div>
          <p style={{ margin: 0, fontSize: 13, color: '#475569', lineHeight: 1.7, maxWidth: 700 }}>
            We use cookies to make our website work better. They improve your experience and help
            with our analytics. By agreeing, you accept our use of cookies as described in our{' '}
            <button onClick={() => { navigate('/privacy-policy'); }}
              style={{ background: 'none', border: 'none', color: '#2563EB', fontWeight: 600, cursor: 'pointer', padding: 0, fontSize: 13, fontFamily: 'inherit' }}>
              Privacy Policy
            </button>.
            {' '}Please see our{' '}
            <button onClick={() => { navigate('/cookie-policy'); }}
              style={{ background: 'none', border: 'none', color: '#2563EB', fontWeight: 600, cursor: 'pointer', padding: 0, fontSize: 13, fontFamily: 'inherit' }}>
              Cookie Policy
            </button>
            {' '}for more detail.
          </p>
        </div>

        {/* Buttons */}
        <div className="cookie-btns">
          <button style={btnPrimary} onClick={acceptAll}
            onMouseEnter={e => e.currentTarget.style.background = '#14305a'}
            onMouseLeave={e => e.currentTarget.style.background = '#1a3a6b'}>
            Accept all
          </button>
          <button style={btnSecondary} onClick={rejectAll}
            onMouseEnter={e => e.currentTarget.style.background = '#14305a'}
            onMouseLeave={e => e.currentTarget.style.background = '#1a3a6b'}>
            Reject all
          </button>
          <button style={btnOutline} onClick={() => setShowPrefs(true)}
            onMouseEnter={e => { e.currentTarget.style.background = '#E2E8F0'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#F1F5F9'; }}>
            Preferences
          </button>
        </div>
      </div>

      {/* ── Preferences modal ── */}
      {showPrefs && (
        <div className="cookie-pref-modal">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <ShieldCheck size={20} color="#16A34A" strokeWidth={2.5} />
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0F172A' }}>Cookie Preferences</h2>
          </div>
          <p style={{ margin: '0 0 24px', fontSize: 13, color: '#64748B', lineHeight: 1.7 }}>
            Choose which cookies you allow. Essential cookies are always active as they are required for the site to function.
          </p>

          {[
            {
              key: 'essential', label: 'Essential', always: true,
              desc: 'Required for login, session management, and core site functionality. Cannot be disabled.',
            },
            {
              key: 'functional', label: 'Functional', always: false,
              desc: 'Remember your preferences, editor state, and settings between sessions.',
            },
            {
              key: 'analytics', label: 'Analytics', always: false,
              desc: 'Help us understand how visitors use MarsResume so we can improve the experience (anonymized).',
            },
          ].map(({ key, label, always, desc }) => (
            <div key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '14px 0', borderBottom: '1px solid #F1F5F9' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0F172A', marginBottom: 3 }}>{label}</div>
                <div style={{ fontSize: 12, color: '#64748B', lineHeight: 1.6 }}>{desc}</div>
              </div>
              {always ? (
                <div style={{ fontSize: 11, fontWeight: 700, color: '#16A34A', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 20, padding: '3px 10px', whiteSpace: 'nowrap', marginTop: 2, flexShrink: 0 }}>
                  Always on
                </div>
              ) : (
                <button
                  onClick={() => setPrefs(p => ({ ...p, [key]: !p[key] }))}
                  style={{
                    width: 46, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer',
                    background: prefs[key] ? '#1a3a6b' : '#CBD5E1',
                    position: 'relative', transition: 'background 0.2s', flexShrink: 0, marginTop: 2,
                  }}>
                  <span style={{
                    position: 'absolute', top: 3, left: prefs[key] ? 23 : 3,
                    width: 20, height: 20, borderRadius: '50%', background: 'white',
                    transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                  }} />
                </button>
              )}
            </div>
          ))}

          <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
            <button style={{ ...btnPrimary, flex: 1 }} onClick={savePrefs}
              onMouseEnter={e => e.currentTarget.style.background = '#14305a'}
              onMouseLeave={e => e.currentTarget.style.background = '#1a3a6b'}>
              Save preferences
            </button>
            <button style={{ ...btnOutline }} onClick={() => setShowPrefs(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
};
